export interface SenderThroughputSample {
  sampledAtMs: number;
  elapsedMs: number;
  bytesSentDelta?: number;
  bitrateBps?: number;
  resetDetected: boolean;
}

type SenderBaseline = {
  streamId: string;
  bytesSent: number;
  sampledAtMs: number;
};

/** Measures one peer's outbound video throughput without confusing counter resets for zero. */
export class SenderVideoBitrateTracker {
  private readonly baselines = new Map<string, SenderBaseline>();

  public constructor(private readonly nowMs: () => number = () => performance.now()) {}

  public observe(peerId: string, streamId: string, bytesSent: number, sampledAtMs = this.nowMs()): SenderThroughputSample {
    const previous = this.baselines.get(peerId);
    const valid = this.valid(streamId, bytesSent, sampledAtMs);
    const streamChanged = previous !== undefined && previous.streamId !== streamId;
    const counterReset = previous !== undefined && bytesSent < previous.bytesSent;
    const elapsedMs = previous === undefined ? 0 : sampledAtMs - previous.sampledAtMs;
    const timeReset = previous !== undefined && (!Number.isFinite(elapsedMs) || elapsedMs <= 0);
    const resetDetected = !valid || streamChanged || counterReset || timeReset;

    if (valid) this.baselines.set(peerId, { streamId, bytesSent, sampledAtMs });
    else this.baselines.delete(peerId);

    if (previous === undefined || resetDetected) {
      return { sampledAtMs, elapsedMs: Math.max(0, Number.isFinite(elapsedMs) ? elapsedMs : 0), resetDetected };
    }

    const bytesSentDelta = bytesSent - previous.bytesSent;
    return {
      sampledAtMs,
      elapsedMs,
      bytesSentDelta,
      bitrateBps: (bytesSentDelta * 8_000) / elapsedMs,
      resetDetected: false,
    };
  }

  public clearPeer(peerId: string): void {
    this.baselines.delete(peerId);
  }

  public clearAll(): void {
    this.baselines.clear();
  }

  private valid(streamId: string, bytesSent: number, sampledAtMs: number): boolean {
    return streamId.length > 0 && Number.isFinite(bytesSent) && bytesSent >= 0 && Number.isFinite(sampledAtMs);
  }
}
